import {isFeatureEnabled} from '@github-ui/feature-flags'
import * as z from 'zod'

export interface SafeOptionalOptions {
  /**
   * When `true`, falls back to a plain `.optional()` instead of the safe
   * transform. Use this during migration to preserve existing behaviour for
   * schemas that haven't been validated against `safeOptional` yet.
   *
   * @default false
   */
  legacyMode?: boolean
}

/**
 * A resilient replacement for raw `.optional()` on complex Zod types.
 *
 * When an optional property has an unexpected or invalid shape, a plain
 * `.optional()` would throw a parse error and potentially break the page.
 * `safeOptional` catches those mismatches and falls back to `undefined`,
 * so malformed optional data is silently ignored instead of crashing the site.
 *
 * Use `legacyMode` to preserve the original `.optional()` behaviour during
 * migration.
 */
export const safeOptional = <T extends z.ZodType>(schema: T, {legacyMode = false}: SafeOptionalOptions = {}) => {
  if (legacyMode) {
    return schema.optional()
  }

  return z
    .unknown()
    .transform((val, ctx): z.infer<T> | undefined => {
      if (val === undefined) {
        return undefined
      }

      const result = schema.safeParse(val)

      if (result.success) {
        return result.data
      }

      // Flag is OFF: surface validation errors through Zod's error system
      // (matching the behaviour of plain .optional()).
      if (!isFeatureEnabled('site_safe_optional')) {
        for (const issue of result.error.issues) {
          ctx.addIssue(issue)
        }
      }

      return undefined
    })
    .optional()
}
